import React, { useState, useRef, useCallback, useMemo, memo } from "react";
import { useTranslation } from "react-i18next";
import QuestionSection from "../../components/ProjectAssessment/QuestionSection";
import ServicesSlider from "../../components/ServicesSlider/ServicesSlider";
import {
  ASSESSMENT_STEPS,
  getPhaseOptions,
  getFocusOptions,
  getPriorityOptions,
} from "../../data/assessmentFromServices";
import { services } from "../../data/services";
import { useRevealOnScroll } from "../../hooks/useRevealOnScroll";
import SectionIntro from "../../components/SectionIntro/SectionIntro";

const ASSESSMENT_INITIAL = {
  projectState: null,
  businessNeed: null,
  requirements: null,
};

const SELECT_KEYS = ["projectState", "businessNeed", "requirements"];

function lastAnsweredStepIndex(so) {
  let last = 0;
  if (so.projectState) last = 0;
  if (so.businessNeed) last = 1;
  if (so.requirements) last = 2;
  return last;
}

const ProjectAssessment = memo(() => {
  const { t, i18n } = useTranslation();
  const introRevealRef = useRevealOnScroll();
  const bodyRevealRef = useRevealOnScroll({
    threshold: 0.06,
    rootMargin: "0px 0px -8% 0px",
  });

  const [selectedOptions, setSelectedOptions] = useState(ASSESSMENT_INITIAL);
  const [expandedQuestion, setExpandedQuestion] = useState(0);
  const selectedOptionsRef = useRef(selectedOptions);
  selectedOptionsRef.current = selectedOptions;

  const handleOptionSelect = useCallback((index, value) => {
    const questionKeys = SELECT_KEYS;
    setSelectedOptions((prev) => {
      let next = { ...prev, [questionKeys[index]]: value };
      if (index === 0 && prev.projectState !== value) {
        next = {
          projectState: value,
          businessNeed: null,
          requirements: null,
        };
      } else if (index === 1 && prev.businessNeed !== value) {
        next = { ...next, requirements: null };
      }

      const order = [
        next.projectState,
        next.businessNeed,
        next.requirements,
      ];
      const nextUnanswered = order.findIndex((v, i) => i > index && !v);
      const expandTo = nextUnanswered === -1 ? index : nextUnanswered;
      setExpandedQuestion(expandTo);

      return next;
    });
  }, []);

  const handleToggle = useCallback((index) => {
    setExpandedQuestion((prev) => {
      if (prev === index) {
        return lastAnsweredStepIndex(selectedOptionsRef.current);
      }
      return index;
    });
  }, []);

  const handleReset = useCallback(() => {
    setSelectedOptions(ASSESSMENT_INITIAL);
    setExpandedQuestion(0);
  }, []);

  const onSelect0 = useCallback((v) => handleOptionSelect(0, v), [handleOptionSelect]);
  const onSelect1 = useCallback((v) => handleOptionSelect(1, v), [handleOptionSelect]);
  const onSelect2 = useCallback((v) => handleOptionSelect(2, v), [handleOptionSelect]);

  const onToggle0 = useCallback(() => handleToggle(0), [handleToggle]);
  const onToggle1 = useCallback(() => handleToggle(1), [handleToggle]);
  const onToggle2 = useCallback(() => handleToggle(2), [handleToggle]);

  const selectHandlers = useMemo(
    () => [onSelect0, onSelect1, onSelect2],
    [onSelect0, onSelect1, onSelect2]
  );
  const toggleHandlers = useMemo(
    () => [onToggle0, onToggle1, onToggle2],
    [onToggle0, onToggle1, onToggle2]
  );

  const stepDerived = useMemo(() => {
    const opts = [
      getPhaseOptions(services, t),
      getFocusOptions(selectedOptions.projectState, services, t),
      getPriorityOptions(
        selectedOptions.projectState,
        selectedOptions.businessNeed,
        services,
        t
      ),
    ];
    const vals = [
      selectedOptions.projectState,
      selectedOptions.businessNeed,
      selectedOptions.requirements,
    ];
    const labels = vals.map((val, i) => {
      if (!val) return null;
      const m = opts[i].find((o) => o.value === val);
      return m ? m.label : null;
    });
    return { opts, labels, vals };
  }, [
    selectedOptions.projectState,
    selectedOptions.businessNeed,
    selectedOptions.requirements,
    t,
  ]);

  const disabledSteps = useMemo(
    () => [
      false,
      !selectedOptions.projectState,
      !selectedOptions.businessNeed,
    ],
    [selectedOptions.projectState, selectedOptions.businessNeed]
  );

  const answeredSteps = useMemo(
    () =>
      [
        selectedOptions.projectState,
        selectedOptions.businessNeed,
        selectedOptions.requirements,
      ].filter(Boolean).length,
    [selectedOptions]
  );

  return (
    <section
      id="project-assessment"
      dir={i18n.dir()}
      className="section-n services-picker section--en pb0 vh-assessment"
      aria-labelledby="assessment-heading"
    >
      <div ref={introRevealRef} className="container-n reveal">
        <div className="pr-big vh-assessment__intro">
          <a className="skip-the-section link-n" href="#panel-9-0-0-3">
            <span className="link-n__text">{t("skip.section")}</span>
          </a>
          <SectionIntro eyebrow={t("assessment.eyebrow")}>
            <h2
              id="assessment-heading"
              className="section__title title-h2 vh-section-title vh-assessment__title"
            >
              {t("assessment.title")}
            </h2>
          </SectionIntro>
          <p className="vh-assessment__lede description-n">
            {t("assessment.lede")}
          </p>
          <div
            className="vh-assessment__progress"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            aria-label={t("assessment.progressAria", {
              answered: answeredSteps,
            })}
          >
            <div className="vh-assessment__progress-track" aria-hidden="true">
              <div
                className="vh-assessment__progress-fill"
                style={{ width: `${(answeredSteps / 3) * 100}%` }}
              />
            </div>
            <span className="vh-assessment__progress-meta">
              <span className="vh-assessment__progress-count">
                {answeredSteps}/3
              </span>
              <span className="vh-assessment__progress-label">
                {t("assessment.answered")}
              </span>
            </span>
          </div>
        </div>
      </div>
      <div
        ref={bodyRevealRef}
        className="services-picker__container container-n reveal reveal--delay-2 vh-assessment__workspace"
      >
        <div className="services-picker__row">
          <div className="services-picker__block">
            <div className="services-picker__questions">
              {ASSESSMENT_STEPS.map((step, index) => (
                <QuestionSection
                  key={step.id}
                  questionNumber={index + 1}
                  title={t(step.titleKey)}
                  stepDescription={t(step.descriptionKey)}
                  options={stepDerived.opts[index]}
                  isDisabled={disabledSteps[index]}
                  isExpanded={expandedQuestion === index}
                  onSelect={selectHandlers[index]}
                  selectedValue={stepDerived.vals[index]}
                  selectedLabel={stepDerived.labels[index]}
                  onToggle={toggleHandlers[index]}
                />
              ))}
            </div>
          </div>
          <ServicesSlider
            services={services}
            selectedOptions={selectedOptions}
            onReset={handleReset}
          />
        </div>
      </div>
    </section>
  );
});

ProjectAssessment.displayName = "ProjectAssessment";

export default ProjectAssessment;
